import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Upload, Trash2, Copy, Check, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMedia() {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/media`, { headers });
      setFiles(res.data);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const uploadedFiles = e.target.files;
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    setUploading(true);

    for (const file of uploadedFiles) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(`${API}/media/upload`, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success(`Uploaded ${file.name}`);
      } catch (error) {
        console.error('Error uploading file:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    fetchFiles();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
      await axios.delete(`${API}/media/${id}`, { headers });
      toast.success('File deleted');
      fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  const copyUrl = async (path) => {
    const url = `${API}/media/files/${path}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(path);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getFileUrl = (path) => `${API}/media/files/${path}`;

  if (loading) {
    return (
      <AdminLayout title="Media Library" subtitle="Manage your images and media files.">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Media Library" subtitle="Manage your images and media files.">
      <div data-testid="admin-media">
        {/* Upload Area */}
        <div className="mb-8">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUpload}
            accept="image/*"
            multiple
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-fire-500/50 transition-colors bg-card/50"
          >
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-fire-500 mb-4"></div>
                <p className="text-white">Uploading...</p>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-gray-500 mb-4" />
                <p className="text-white font-medium">Click to upload images</p>
                <p className="text-gray-500 text-sm">or drag and drop</p>
              </>
            )}
          </label>
        </div>

        {/* Files Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {files.map(file => (
            <div
              key={file.id}
              className="group relative bg-card rounded-xl border border-white/10 overflow-hidden aspect-square"
              data-testid={`media-file-${file.id}`}
            >
              {file.content_type?.startsWith('image/') ? (
                <img
                  src={getFileUrl(file.storage_path)}
                  alt={file.original_filename}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-black/50">
                  <ImageIcon className="w-12 h-12 text-gray-500" />
                </div>
              )}

              {/* Overlay */}
              <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-4">
                <p className="text-white text-sm text-center truncate w-full">{file.original_filename}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyUrl(file.storage_path)}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    {copiedId === file.storage_path ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(file.id)}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {files.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No media files yet</p>
            <p className="text-sm">Upload some images to get started</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
